import React, { useState, useEffect, useMemo } from 'react';
import { FloatingCalendarNav } from './FloatingCalendarNav';
import {
    Box,
    VStack,
    HStack,
    Stack,
    Text,
    Button,
    ButtonGroup,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Table,
    TableContainer,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Textarea,
    Checkbox,
    Divider,
    SimpleGrid,
    FormControl,
    FormLabel,
    Icon,
    Link,
} from "@chakra-ui/react";
import { Tooltip } from "./components/Tip";
import {
  FiPlus, FiTrash2, FiCalendar, FiUser, FiEdit, FiEye, FiMail, FiCopy, FiShare2, FiUserCheck, FiClock,
  FiExternalLink, FiSettings, FiGrid, FiList, FiSearch, FiInfo, FiAlertTriangle, FiGlobe, FiLink,
} from 'react-icons/fi';
import { TriangleUpIcon, TriangleDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useQuery, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { usePageTitle } from "../../hooks/useDocumentTitle";
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import ExternalCalendarModal from "./ExternalCalendarModal";
import ShareCalendarModal from "./components/ShareCalendarModal";
import { CalendarType, CalendarVisibility } from "../../generated/graphql";
import { PoweredBy } from "./components/PoweredBy";
import {
  useCalTheme,
  alpha,
  CalPageHeader,
  CalSurface,
  CalStatCard,
  CalStatusPill,
  calWords,
  CalLoading,
  CalError,
  CalEmpty,
  useCalConfirm,
} from "./components/ui";
import {
  GET_ALL_CALENDARS,
  GET_EXTERNAL_CALENDARS_ADMIN as GET_EXTERNAL_CALENDARS,
  CREATE_CALENDAR_ADMIN as CREATE_CALENDAR,
  DELETE_CALENDAR,
  GET_CLIENTS_ADMIN as GET_CLIENTS,
  TRANSFER_OWNERSHIP,
} from './queries';

// Sortable table columns (client-side sort over the loaded calendar list)
type CalendarSortField = 'name' | 'type' | 'visibility' | 'owner' | 'events' | 'createdAt';

// Filters/sort/page-size persisted per-browser, mirroring the employees
// page's "employeesListFilters" approach (module-local — no employees imports).
const CALENDAR_ADMIN_FILTERS_KEY = 'calendarAdminFilters';

const getSavedFilters = (): {
  searchTerm?: string;
  filterType?: string;
  filterVisibility?: string;
  sortBy?: CalendarSortField;
  sortOrder?: 'asc' | 'desc';
  pageSize?: number;
  viewMode?: 'table' | 'cards';
} => {
  try {
    const saved = localStorage.getItem(CALENDAR_ADMIN_FILTERS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const CalendarAccountsAdmin: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  // Calendar sharing is now managed via Email module (EmailAddress.linkedCalendars)
  const { isOpen: isTransferOpen, onOpen: onTransferOpen, onClose: onTransferClose } = useDisclosure();
  const { isOpen: isExternalOpen, onOpen: onExternalOpen, onClose: onExternalClose } = useDisclosure();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();
  const { confirm, confirmDialog } = useCalConfirm();

  const savedFilters = useMemo(getSavedFilters, []);

  const [selectedCalendar, setSelectedCalendar] = useState<any>(null);
  // Calendar selected for the Share modal (kept separate from selectedCalendar
  // so an open Edit/Transfer modal can't be clobbered)
  const [shareCalendar, setShareCalendar] = useState<any>(null);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [searchTerm, setSearchTerm] = useState(savedFilters.searchTerm || '');
  const [debouncedSearch, setDebouncedSearch] = useState(savedFilters.searchTerm || '');
  const [filterType, setFilterType] = useState(savedFilters.filterType || 'all');
  const [filterVisibility, setFilterVisibility] = useState(savedFilters.filterVisibility || 'all');
  const [sortBy, setSortBy] = useState<CalendarSortField>(savedFilters.sortBy || 'createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(savedFilters.sortOrder || 'desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(savedFilters.pageSize || 20);
  // Wide screens choose table or cards; phones and tablets always get cards.
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(savedFilters.viewMode === 'cards' ? 'cards' : 'table');
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<Set<string>>(new Set());

  // Debounce the search input (300ms) so filtering doesn't run per keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Persist filters/sort/page size so the page remembers how you left it
  useEffect(() => {
    localStorage.setItem(CALENDAR_ADMIN_FILTERS_KEY, JSON.stringify({
      searchTerm,
      filterType,
      filterVisibility,
      sortBy,
      sortOrder,
      pageSize,
      viewMode,
    }));
  }, [searchTerm, filterType, filterVisibility, sortBy, sortOrder, pageSize, viewMode]);

  // Update page title
  usePageTitle('Calendar Admin');

  // State for creating new calendar
  const [newCalendarName, setNewCalendarName] = useState('');
  const [newCalendarDescription, setNewCalendarDescription] = useState('');
  const [newCalendarType, setNewCalendarType] = useState('BUSINESS');
  const [newCalendarVisibility, setNewCalendarVisibility] = useState('PRIVATE');
  const [newCalendarColor, setNewCalendarColor] = useState('#4A90E2');
  const [newCalendarTimezone, setNewCalendarTimezone] = useState('Australia/Sydney');

  // GraphQL queries
  const { data: calendarsData, loading: calendarsLoading, error: calendarsError, refetch: refetchCalendars } = useQuery(GET_ALL_CALENDARS);
  const { refetch: refetchExternalCalendars } = useQuery(GET_EXTERNAL_CALENDARS);
  const { data: clientsData, loading: clientsLoading } = useQuery(GET_CLIENTS);

  // Debug logging
  if (calendarsError) {
    console.error('Error fetching calendars:', calendarsError);
  }

  // Helper function to get owner details
  const getOwnerDetails = (ownerId: string) => {
    if (!clientsData?.clients) return null;
    return clientsData.clients.find((client: any) => client.id === ownerId);
  };


  // GraphQL mutations
  const [createCalendar] = useMutation(CREATE_CALENDAR, {
    onCompleted: (data) => {
      toast({
        title: 'Calendar created successfully',
        description: `Created ${data.createCalendar.name}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      refetchCalendars();
      onCreateClose();
      // Reset form
      setNewCalendarName('');
      setNewCalendarDescription('');
      setNewCalendarType('BUSINESS');
      setNewCalendarVisibility('PRIVATE');
      setNewCalendarColor('#4A90E2');
      setNewCalendarTimezone('Australia/Sydney');
    },
    onError: (error) => {
      toast({
        title: 'Error creating calendar',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const [deleteCalendar] = useMutation(DELETE_CALENDAR, {
    onCompleted: () => {
      toast({
        title: 'Calendar deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetchCalendars();
    },
    onError: (error) => {
      toast({
        title: 'Error deleting calendar',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Accepted-email management removed — email→calendar routing is managed via the
  // Email module (EmailAddress.linkedCalendars); the old accepted-email mutations no
  // longer exist in the backend schema.

  const [transferOwnership] = useMutation(TRANSFER_OWNERSHIP, {
    onCompleted: () => {
      toast({
        title: 'Ownership transferred successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetchCalendars();
      onTransferClose();
      setNewOwnerId('');
    },
    onError: (error) => {
      toast({
        title: 'Error transferring ownership',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const t = useCalTheme();

  const calendars = calendarsData?.calendars || [];

  // Get enum values from generated types
  // Plain words from the module's one enum map; a picker list is A–Z (rule 45).
  const byLabel = (a: { label: string }, b: { label: string }) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true });
  const calendarTypes = Object.values(CalendarType)
    .map((value) => ({ name: value, label: calWords('type', value) }))
    .sort(byLabel);

  const calendarVisibilities = Object.values(CalendarVisibility)
    .map((value) => ({ name: value, label: calWords('visibility', value) }))
    .sort(byLabel);

  // Debug logging
  console.log('Calendars data:', {
    calendarsData,
    calendars,
    count: calendars.length,
    loading: calendarsLoading,
    error: calendarsError,
    calendarTypes,
    calendarVisibilities
  });

  // Filter calendars based on (debounced) search and filters.
  // Search covers name, description, and the owner's name/email.
  const filteredCalendars = calendars.filter((calendar: any) => {
    const search = debouncedSearch.trim().toLowerCase();
    const owner = calendar.responsibleOwnerId ? getOwnerDetails(calendar.responsibleOwnerId) : null;
    const ownerText = owner
      ? `${owner.fName || ''} ${owner.lName || ''} ${owner.email || ''}`.toLowerCase()
      : '';
    const matchesSearch = !search ||
      calendar.name.toLowerCase().includes(search) ||
      calendar.description?.toLowerCase().includes(search) ||
      ownerText.includes(search);
    const matchesType = filterType === 'all' || calendar.type === filterType;
    const matchesVisibility = filterVisibility === 'all' || calendar.visibility === filterVisibility;

    return matchesSearch && matchesType && matchesVisibility;
  });

  // Client-side sort over the filtered list (all data is already loaded)
  const sortValue = (calendar: any): string | number => {
    switch (sortBy) {
      case 'name': return (calendar.name || '').toLowerCase();
      case 'type': return calendar.type || '';
      case 'visibility': return calendar.visibility || '';
      case 'owner': {
        const owner = calendar.responsibleOwnerId ? getOwnerDetails(calendar.responsibleOwnerId) : null;
        return owner ? `${owner.fName || ''} ${owner.lName || ''}`.trim().toLowerCase() : '';
      }
      case 'events': return calendar.totalEvents || 0;
      case 'createdAt': return calendar.createdAt ? new Date(calendar.createdAt).getTime() : 0;
      default: return '';
    }
  };

  const sortedCalendars = [...filteredCalendars].sort((a: any, b: any) => {
    const aVal = sortValue(a);
    const bVal = sortValue(b);
    let cmp = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      cmp = aVal - bVal;
    } else {
      cmp = String(aVal).localeCompare(String(bVal));
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  // Toggle a sort column (asc <-> desc), or switch to it ascending
  const toggleSort = (field: CalendarSortField) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Client-side pagination (table + cards share the same page slice)
  const totalFiltered = sortedCalendars.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCalendars = sortedCalendars.slice((safePage - 1) * pageSize, safePage * pageSize);
  const showingFrom = totalFiltered === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = Math.min(safePage * pageSize, totalFiltered);

  const handleCreateCalendar = async () => {
    if (!newCalendarName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a calendar name',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    await createCalendar({
      variables: {
        input: {
          name: newCalendarName,
          description: newCalendarDescription || undefined,
          type: newCalendarType,
          visibility: newCalendarVisibility,
          color: newCalendarColor,
          settings: {
            timezone: newCalendarTimezone,
            emailNotifications: true,
            smsNotifications: false,
            defaultReminderMinutes: 15
          }
        },
      },
    });
  };

  const handleDeleteCalendar = async (calendar: any) => {
    const ok = await confirm({
      title: `Delete ${calendar?.name ? `“${calendar.name}”` : 'this calendar'}?`,
      body: "The calendar is switched off and disappears from every list and view. Its events are not deleted, but they stop showing with it. There is no undo on this site — only an administrator can bring it back. Only the calendar's owner can delete it.",
      confirmLabel: 'Delete calendar',
      cancelLabel: 'Keep calendar',
      tone: 'danger',
    });
    if (ok) {
      await deleteCalendar({
        variables: { id: calendar.id },
      });
    }
  };

  /** Rule 46: a calendar is edited in place on its own page. Back returns here. */
  const openCalendarPage = (calendar: any, edit: boolean) => {
    navigate(`/calendars/${calendar.id}?section=basics${edit ? '&edit=1' : ''}`, {
      state: { backTo: '/calendar/admin/calendars', backLabel: 'Calendar management' },
    });
  };

  const openTransferModal = (calendar: any) => {
    setSelectedCalendar(calendar);
    setNewOwnerId('');
    onTransferOpen();
  };

  const handleTransferOwnership = async () => {
    if (!selectedCalendar) return;

    await transferOwnership({
      variables: {
        calendarId: selectedCalendar.id,
        newOwnerId: newOwnerId || undefined
      }
    });
  };

  const handleCopyToClipboard = (text: string, label: string = 'Text') => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: `${label} copied to clipboard`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }).catch(() => {
      toast({
        title: 'Failed to copy',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    });
  };


  // Toggle calendar selection
  const toggleCalendarSelection = (calendarId: string) => {
    setSelectedCalendarIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(calendarId)) {
        newSet.delete(calendarId);
      } else {
        newSet.add(calendarId);
      }
      return newSet;
    });
  };

  // Select/Deselect all calendars on the current page
  const allOnPageSelected = paginatedCalendars.length > 0 &&
    paginatedCalendars.every((c: any) => selectedCalendarIds.has(c.id));
  const someOnPageSelected = paginatedCalendars.some((c: any) => selectedCalendarIds.has(c.id));
  const toggleAllCalendars = () => {
    setSelectedCalendarIds(prev => {
      const newSet = new Set(prev);
      if (allOnPageSelected) {
        paginatedCalendars.forEach((c: any) => newSet.delete(c.id));
      } else {
        paginatedCalendars.forEach((c: any) => newSet.add(c.id));
      }
      return newSet;
    });
  };

  // Open the Share modal for a calendar
  const openShareModal = (calendar: any) => {
    setShareCalendar(calendar);
    onShareOpen();
  };

  // Open selected calendars in new tab
  const openMultipleCalendars = () => {
    if (selectedCalendarIds.size === 0) {
      toast({
        title: 'No calendars selected',
        description: 'Please select at least one calendar to view',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const calendarIds = Array.from(selectedCalendarIds).join(',');
    const url = `/calendars/view?calendars=${calendarIds}`;
    window.open(url, '_blank');

    // Clear selection after opening
    setSelectedCalendarIds(new Set());
  };

  // ─── Shared styling (kit tokens only — reads in light and dark) ───
  const fieldProps = {
    size: 'lg' as const,
    bg: t.raised,
    borderColor: t.borderStrong,
    color: t.text,
    _placeholder: { color: t.faint },
  };
  const labelProps = { fontSize: 'md', fontWeight: 'semibold', color: t.text, mb: 2 };
  const isFiltered = debouncedSearch.trim() !== '' || filterType !== 'all' || filterVisibility !== 'all';
  const tickedOffPage = Array.from(selectedCalendarIds).filter(
    (id) => !paginatedCalendars.some((c: any) => c.id === id),
  ).length;
  const totalEvents = calendars.reduce((sum: number, cal: any) => sum + (cal.totalEvents || 0), 0);
  const publicCount = calendars.filter((c: any) => c.visibility === 'PUBLIC').length;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const createdLabel = (value?: string) => {
    if (!value) return 'Unknown';
    const d = new Date(value);
    return isNaN(d.getTime()) ? 'Unknown' : format(d, 'd MMM yyyy');
  };
  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setFilterType('all');
    setFilterVisibility('all');
    setCurrentPage(1);
  };

  const header = (
    <CalPageHeader
      icon={FiCalendar}
      eyebrow="Calendars · Admin"
      title="Calendar management"
      subtitle="Every calendar in the business: who owns it, who can see it, and how busy it is. Create, share, hand over or remove calendars from here."
      actions={
        <>
          <Tooltip label="Create a brand new calendar. Opens a form; nothing is saved until you press Create calendar." hasArrow openDelay={300}>
            <Button leftIcon={<FiPlus />} colorScheme="blue" size="lg" minH="48px" onClick={onCreateOpen}>
              Create calendar
            </Button>
          </Tooltip>
          <Tooltip label="Connect a calendar from Google or Outlook. Opens a form; nothing is saved until you confirm." hasArrow openDelay={300}>
            <Button leftIcon={<FiShare2 />} variant="outline" size="lg" minH="48px" onClick={onExternalOpen}>
              Add external calendar
            </Button>
          </Tooltip>
        </>
      }
    />
  );

  if (calendarsLoading || clientsLoading) {
    return (
      <Box p={{ base: 4, md: 6 }} maxW="100%" overflowX="hidden">
        <FloatingCalendarNav />
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          {header}
          <CalLoading label="Loading every calendar and its owner" cards={6} />
        </VStack>
      </Box>
    );
  }

  if (calendarsError) {
    return (
      <Box p={{ base: 4, md: 6 }} maxW="100%" overflowX="hidden">
        <FloatingCalendarNav />
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          {header}
          <CalError title="The calendar list could not be loaded" error={calendarsError} onRetry={() => refetchCalendars()} />
        </VStack>
      </Box>
    );
  }

  // Sortable column header — a real button (keyboard + 44px), arrow AND words say the direction.
  const sortHeader = (field: CalendarSortField, label: string) => {
    const active = sortBy === field;
    return (
      <Th
        key={field}
        textTransform="none"
        letterSpacing="normal"
        fontSize="sm"
        color={t.muted}
        px={2}
        aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <Tooltip label={`Sort by ${label.toLowerCase()}. Press again to reverse the order. Changes only what you see.`} hasArrow openDelay={300}>
          <Button
            variant="ghost"
            size="sm"
            minH="44px"
            px={2}
            fontSize="sm"
            fontWeight="semibold"
            color={active ? t.text : t.muted}
            onClick={() => toggleSort(field)}
            rightIcon={active ? (sortOrder === 'asc' ? <TriangleUpIcon boxSize={2.5} /> : <TriangleDownIcon boxSize={2.5} />) : undefined}
          >
            {label}
          </Button>
        </Tooltip>
      </Th>
    );
  };

  const plainTh = (label: string) => (
    <Th textTransform="none" letterSpacing="normal" fontSize="sm" color={t.muted} px={2}>
      {label}
    </Th>
  );

  // Calendar name → the calendar itself (a real link: middle-click / copy link work).
  const nameLink = (calendar: any, size: 'md' | 'lg') => (
    <Link
      as={RouterLink}
      to={`/calendars/${calendar.id}/view`}
      color={t.text}
      fontWeight="semibold"
      fontSize={size}
      sx={{ overflowWrap: 'anywhere' }}
      _hover={{ color: t.primary, textDecoration: 'underline' }}
    >
      {calendar.name}
    </Link>
  );

  const eventsText = (calendar: any) =>
    `${plural(calendar.totalEvents || 0, 'event')} · ${calendar.upcomingEvents || 0} upcoming`;

  // One card per calendar — the phone layout, and the "Cards" choice on wide screens.
  const renderCard = (calendar: any) => {
    const ownerDetails = calendar.responsibleOwnerId ? getOwnerDetails(calendar.responsibleOwnerId) : null;
    const bookingUrl = calendar.bookingPageSlug
      ? `${window.location.origin}/book/${calendar.bookingPageSlug}`
      : null;

    return (
      <CalSurface key={calendar.id} interactive>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="start" spacing={3}>
            {/* minW=0 — external calendars imported from Google arrive with a URL as their name;
                without it the unbreakable string pushes the checkbox off-screen. */}
            <HStack spacing={3} flex={1} minW={0} align="start">
              <Box w={4} h={4} mt={1.5} borderRadius="full" bg={calendar.color || t.primary} flexShrink={0} />
              <VStack align="start" spacing={1} flex={1} minW={0}>
                {nameLink(calendar, 'lg')}
                {calendar.description && (
                  <Text fontSize="md" color={t.muted} noOfLines={3} sx={{ overflowWrap: 'anywhere' }}>
                    {calendar.description}
                  </Text>
                )}
              </VStack>
            </HStack>
            <Checkbox
              size="lg"
              isChecked={selectedCalendarIds.has(calendar.id)}
              onChange={() => toggleCalendarSelection(calendar.id)}
              colorScheme="purple"
              flexShrink={0}
            >
              <Text as="span" fontSize="sm" color={t.muted}>Pick</Text>
            </Checkbox>
          </HStack>

          <HStack spacing={2} flexWrap="wrap" rowGap={2}>
            <CalStatusPill kind="type" status={calendar.type} />
            <CalStatusPill kind="visibility" status={calendar.visibility} />
          </HStack>

          <VStack align="stretch" spacing={1.5}>
            <HStack spacing={2}>
              <Icon as={FiCalendar} color={t.faint} flexShrink={0} />
              <Text fontSize="sm" color={t.muted}>{eventsText(calendar)}</Text>
            </HStack>
            <HStack spacing={2}>
              <Icon as={FiClock} color={t.faint} flexShrink={0} />
              <Text fontSize="sm" color={t.muted}>Created {createdLabel(calendar.createdAt)}</Text>
            </HStack>
            {calendar.sharedFromEmail && (
              <HStack spacing={2} align="start">
                <Icon as={FiMail} color={t.faint} flexShrink={0} mt={1} />
                <Text fontSize="sm" color={t.muted} sx={{ overflowWrap: 'anywhere' }}>
                  Shared from {calendar.sharedFromEmail}
                </Text>
              </HStack>
            )}
          </VStack>

          {/* Owner */}
          <Box p={3} bg={t.raised} borderRadius="lg" borderWidth="1px" borderColor={t.border}>
            {!calendar.responsibleOwnerId ? (
              <Text fontSize="sm" color={t.muted}>No owner assigned</Text>
            ) : ownerDetails ? (
              <>
                <HStack spacing={2}>
                  <Icon as={FiUser} color={t.faint} flexShrink={0} />
                  <Text fontSize="md" color={t.text} fontWeight="semibold">
                    {ownerDetails.fName} {ownerDetails.lName}
                  </Text>
                </HStack>
                {ownerDetails.email && (
                  <HStack spacing={2} mt={1} align="start">
                    <Icon as={FiMail} color={t.faint} flexShrink={0} mt={1} />
                    <Text fontSize="sm" color={t.muted} sx={{ overflowWrap: 'anywhere' }}>
                      {ownerDetails.email}
                    </Text>
                  </HStack>
                )}
              </>
            ) : (
              <Text fontSize="sm" color={t.muted} sx={{ overflowWrap: 'anywhere' }}>
                Owner ID {calendar.responsibleOwnerId} (not in the client list)
              </Text>
            )}
          </Box>

          <Divider borderColor={t.border} />

          <SimpleGrid columns={2} spacing={2}>
            <Tooltip label="Open this calendar's own page on its settings. Each part there has its own Edit button; nothing changes until you press Save." hasArrow openDelay={300}>
              <Button size="md" minH="44px" leftIcon={<FiSettings />} variant="outline" colorScheme="blue" onClick={() => openCalendarPage(calendar, false)}>
                Full settings
              </Button>
            </Tooltip>
            <Tooltip label="Share this calendar with other people and choose who can view or edit it. Opens a form first." hasArrow openDelay={300}>
              <Button size="md" minH="44px" leftIcon={<FiShare2 />} variant="outline" colorScheme="green" onClick={() => openShareModal(calendar)}>
                Share
              </Button>
            </Tooltip>
            {calendar.allowPublicBooking && (
              <Tooltip label="Set up the kinds of appointments people can book" hasArrow openDelay={300}>
                <Button size="md" minH="44px" leftIcon={<FiCalendar />} variant="outline" colorScheme="purple" onClick={() => navigate(`/calendars/${calendar.id}/event-types`)}>
                  Event types
                </Button>
              </Tooltip>
            )}
            {calendar.allowPublicBooking && (
              <Tooltip label="Set the days and hours when people can book time" hasArrow openDelay={300}>
                <Button size="md" minH="44px" leftIcon={<FiClock />} variant="outline" colorScheme="green" onClick={() => navigate(`/calendars/${calendar.id}/availability`)}>
                  Availability
                </Button>
              </Tooltip>
            )}
            {bookingUrl && (
              <Tooltip label="Copy the public booking link so you can send it to someone. Changes nothing." hasArrow openDelay={300}>
                <Button size="md" minH="44px" leftIcon={<FiLink />} variant="outline" colorScheme="teal" onClick={() => handleCopyToClipboard(bookingUrl, 'Booking link')}>
                  Copy link
                </Button>
              </Tooltip>
            )}
            <Tooltip label="Hand this calendar over to a different owner. Opens a form first." hasArrow openDelay={300}>
              <Button size="md" minH="44px" leftIcon={<FiUserCheck />} variant="outline" colorScheme="orange" onClick={() => openTransferModal(calendar)}>
                Transfer
              </Button>
            </Tooltip>
            <Tooltip label="Switch this calendar off so it disappears from every list and view (its events are kept). You are asked to confirm first; only the owner can delete it." hasArrow openDelay={300}>
              <Button size="md" minH="44px" leftIcon={<FiTrash2 />} variant="outline" colorScheme="red" onClick={() => handleDeleteCalendar(calendar)}>
                Delete
              </Button>
            </Tooltip>
          </SimpleGrid>
        </VStack>
      </CalSurface>
    );
  };

  return (
    <Box p={{ base: 4, md: 6 }} maxW="100%" overflowX="hidden">
        <FloatingCalendarNav />
        <VStack spacing={{ base: 4, md: 6 }} align="stretch" minW={0}>
          {header}

          {/* Stats — whole system, before any search or filter */}
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={{ base: 3, md: 4 }}>
            <CalStatCard
              label="Calendars"
              value={calendars.length}
              hint="Every calendar, before filters"
              icon={FiCalendar}
              tone="accent"
              tip="How many calendars exist in the business, counting every type and owner."
            />
            <CalStatCard
              label="Events"
              value={totalEvents}
              hint={`Across all ${plural(calendars.length, 'calendar')}`}
              icon={FiClock}
              tone="success"
              tip="Events added together across every calendar, past and upcoming."
            />
            <CalStatCard
              label="Public calendars"
              value={publicCount}
              hint="Anyone can see these"
              icon={FiGlobe}
              tone="warning"
              tip="Calendars whose visibility is Public, so anyone with the link can see them."
            />
          </SimpleGrid>

          {/* Search + filters */}
          <CalSurface>
            <Stack spacing={3}>
              <FormControl>
                <FormLabel htmlFor="cal-admin-search" {...labelProps}>Find a calendar</FormLabel>
                <InputGroup size="lg">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiSearch} color={t.faint} />
                  </InputLeftElement>
                  <Input
                    id="cal-admin-search"
                    placeholder="Name, description, or owner"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    {...fieldProps}
                  />
                </InputGroup>
              </FormControl>
              <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align={{ base: 'stretch', md: 'end' }}>
                <FormControl flex={1} minW={0}>
                  <FormLabel htmlFor="cal-admin-type" {...labelProps}>Type</FormLabel>
                  <Tooltip label="Show only calendars of one type. Changes only what you see." hasArrow openDelay={300}>
                    <Box w="100%">
                      <Select
                        id="cal-admin-type"
                        value={filterType}
                        onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
                        {...fieldProps}
                      >
                        <option value="all">All types</option>
                        {calendarTypes.map((type) => (
                          <option key={type.name} value={type.name}>
                            {type.label}
                          </option>
                        ))}
                      </Select>
                    </Box>
                  </Tooltip>
                </FormControl>
                <FormControl flex={1} minW={0}>
                  <FormLabel htmlFor="cal-admin-visibility" {...labelProps}>Who can see it</FormLabel>
                  <Tooltip label="Show only calendars with one visibility setting. Changes only what you see." hasArrow openDelay={300}>
                    <Box w="100%">
                      <Select
                        id="cal-admin-visibility"
                        value={filterVisibility}
                        onChange={(e) => { setFilterVisibility(e.target.value); setCurrentPage(1); }}
                        {...fieldProps}
                      >
                        <option value="all">Any visibility</option>
                        {calendarVisibilities.map((visibility) => (
                          <option key={visibility.name} value={visibility.name}>
                            {visibility.label}
                          </option>
                        ))}
                      </Select>
                    </Box>
                  </Tooltip>
                </FormControl>
                {isFiltered && (
                  <Tooltip label="Clear the search and both filters to show every calendar again. Changes only what you see." hasArrow openDelay={300}>
                    <Button size="lg" minH="48px" variant="ghost" onClick={clearFilters} flexShrink={0}>
                      Clear filters
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </CalSurface>

          {/* Result line + view choice + bulk open */}
          {calendars.length > 0 && (
            <Stack
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align={{ base: 'stretch', md: 'center' }}
              spacing={3}
            >
              <Box minW={0}>
                <Text fontSize="md" color={t.text} fontWeight="semibold">
                  {totalFiltered === 0
                    ? 'No calendars match'
                    : `Showing ${showingFrom}–${showingTo} of ${plural(totalFiltered, 'calendar')}`}
                </Text>
                <Text fontSize="sm" color={t.muted}>
                  {isFiltered
                    ? `${totalFiltered} of ${calendars.length} match your search and filters.`
                    : `That is every calendar in the business.`}
                  {totalPages > 1 && ` The rest are on the next ${totalPages - safePage === 1 ? 'page' : 'pages'} — use Next below.`}
                </Text>
              </Box>
              <HStack spacing={2} flexWrap="wrap" rowGap={2}>
                {selectedCalendarIds.size > 0 && (
                  <>
                    <Text fontSize="sm" color={t.muted}>
                      {plural(selectedCalendarIds.size, 'calendar')} picked
                      {tickedOffPage > 0 && ` (${tickedOffPage} on other pages)`}
                    </Text>
                    <Tooltip label="Open the picked calendars together in one combined view, in a new tab. Changes nothing." hasArrow openDelay={300}>
                      <Button
                        leftIcon={<FiEye />}
                        colorScheme="purple"
                        onClick={openMultipleCalendars}
                        size="md"
                        minH="44px"
                        variant="outline"
                      >
                        View {plural(selectedCalendarIds.size, 'calendar')} together
                      </Button>
                    </Tooltip>
                  </>
                )}
                <ButtonGroup size="md" isAttached variant="outline" display={{ base: 'none', lg: 'inline-flex' }}>
                  <Tooltip label="Show calendars as a table. Remembered in this browser." hasArrow openDelay={300}>
                    <Button
                      minH="44px"
                      leftIcon={<FiList />}
                      isActive={viewMode === 'table'}
                      aria-pressed={viewMode === 'table'}
                      onClick={() => setViewMode('table')}
                    >
                      Table
                    </Button>
                  </Tooltip>
                  <Tooltip label="Show calendars as cards with every shortcut. Remembered in this browser." hasArrow openDelay={300}>
                    <Button
                      minH="44px"
                      leftIcon={<FiGrid />}
                      isActive={viewMode === 'cards'}
                      aria-pressed={viewMode === 'cards'}
                      onClick={() => setViewMode('cards')}
                    >
                      Cards
                    </Button>
                  </Tooltip>
                </ButtonGroup>
              </HStack>
            </Stack>
          )}

          {calendars.length === 0 ? (
            <CalEmpty
              icon={FiCalendar}
              title="No calendars yet"
              action={
                <Tooltip label="Create a brand new calendar. Opens a form first." hasArrow openDelay={300}>
                  <Button leftIcon={<FiPlus />} colorScheme="blue" size="lg" minH="48px" onClick={onCreateOpen}>
                    Create calendar
                  </Button>
                </Tooltip>
              }
            >
              Nobody in the business has a calendar yet. Create one here, or connect one from Google or Outlook.
            </CalEmpty>
          ) : totalFiltered === 0 ? (
            <CalEmpty
              icon={FiSearch}
              title="No calendars match"
              action={
                <Tooltip label="Clear the search and both filters to show every calendar again." hasArrow openDelay={300}>
                  <Button size="lg" minH="48px" variant="outline" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </Tooltip>
              }
            >
              {`None of the ${plural(calendars.length, 'calendar')} match your search and filters.`}
            </CalEmpty>
          ) : (
            <>
              {/* Table — wide screens only, when chosen */}
              <Box display={{ base: 'none', lg: viewMode === 'table' ? 'block' : 'none' }}>
                <CalSurface p={{ base: 2, md: 4 }}>
                  <TableContainer overflowX="auto" whiteSpace="normal">
                    <Table variant="simple" size="md">
                      <Thead>
                        <Tr>
                          <Th width="56px" px={2} borderColor={t.border}>
                            <Checkbox
                              size="lg"
                              aria-label="Pick every calendar on this page"
                              isChecked={allOnPageSelected}
                              isIndeterminate={someOnPageSelected && !allOnPageSelected}
                              onChange={toggleAllCalendars}
                              colorScheme="purple"
                            />
                          </Th>
                          {sortHeader('name', 'Name')}
                          {sortHeader('type', 'Type')}
                          {sortHeader('visibility', 'Who can see it')}
                          {sortHeader('owner', 'Owner')}
                          {plainTh('Shared from')}
                          {sortHeader('events', 'Events')}
                          {sortHeader('createdAt', 'Created')}
                          {plainTh('Actions')}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {paginatedCalendars.map((calendar: any) => {
                          const ownerDetails = calendar.responsibleOwnerId ? getOwnerDetails(calendar.responsibleOwnerId) : null;
                          return (
                            <Tr key={calendar.id} _hover={{ bg: t.surfaceHover }}>
                              <Td width="56px" px={2} borderColor={t.border}>
                                <Checkbox
                                  size="lg"
                                  aria-label={`Pick ${calendar.name}`}
                                  isChecked={selectedCalendarIds.has(calendar.id)}
                                  onChange={() => toggleCalendarSelection(calendar.id)}
                                  colorScheme="purple"
                                />
                              </Td>
                              <Td px={2} minW="200px" maxW="320px" borderColor={t.border}>
                                <HStack spacing={2} align="start">
                                  <Box w={3} h={3} mt={1.5} borderRadius="full" bg={calendar.color || t.primary} flexShrink={0} />
                                  <VStack align="start" spacing={0} minW={0}>
                                    {nameLink(calendar, 'md')}
                                    {calendar.description && (
                                      <Text fontSize="sm" color={t.muted} noOfLines={2} sx={{ overflowWrap: 'anywhere' }}>
                                        {calendar.description}
                                      </Text>
                                    )}
                                  </VStack>
                                </HStack>
                              </Td>
                              <Td px={2} borderColor={t.border}>
                                <CalStatusPill kind="type" status={calendar.type} />
                              </Td>
                              <Td px={2} borderColor={t.border}>
                                <CalStatusPill kind="visibility" status={calendar.visibility} />
                              </Td>
                              <Td px={2} minW="220px" borderColor={t.border}>
                                <VStack align="start" spacing={1}>
                                  {!calendar.responsibleOwnerId ? (
                                    <Text color={t.muted} fontSize="sm">No owner assigned</Text>
                                  ) : ownerDetails ? (
                                    <>
                                      <HStack spacing={2}>
                                        <Icon as={FiUser} color={t.faint} flexShrink={0} />
                                        <Text color={t.text} fontSize="sm" fontWeight="semibold">
                                          {ownerDetails.fName} {ownerDetails.lName}
                                        </Text>
                                      </HStack>
                                      {ownerDetails.email && (
                                        <HStack spacing={2} align="start">
                                          <Icon as={FiMail} color={t.faint} flexShrink={0} mt={1} />
                                          <Text color={t.muted} fontSize="sm" sx={{ overflowWrap: 'anywhere' }}>
                                            {ownerDetails.email}
                                          </Text>
                                        </HStack>
                                      )}
                                    </>
                                  ) : (
                                    <Text color={t.muted} fontSize="sm" sx={{ overflowWrap: 'anywhere' }}>
                                      Owner ID {calendar.responsibleOwnerId} (not in the client list)
                                    </Text>
                                  )}
                                  <HStack spacing={1} flexWrap="wrap">
                                    {calendar.responsibleOwnerId && (
                                      <Tooltip label="Copy the owner's client ID to your clipboard. Changes nothing." hasArrow openDelay={300}>
                                        <Button
                                          size="sm"
                                          minH="44px"
                                          fontSize="sm"
                                          variant="ghost"
                                          leftIcon={<FiCopy />}
                                          color={t.muted}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyToClipboard(calendar.responsibleOwnerId, ownerDetails ? 'Client ID' : 'Owner ID');
                                          }}
                                        >
                                          Copy ID
                                        </Button>
                                      </Tooltip>
                                    )}
                                    <Tooltip label="Hand this calendar over to a different owner. Opens a form first." hasArrow openDelay={300}>
                                      <Button
                                        size="sm"
                                        minH="44px"
                                        fontSize="sm"
                                        variant="ghost"
                                        colorScheme="orange"
                                        leftIcon={<FiUserCheck />}
                                        onClick={() => openTransferModal(calendar)}
                                      >
                                        Transfer
                                      </Button>
                                    </Tooltip>
                                  </HStack>
                                </VStack>
                              </Td>
                              <Td px={2} maxW="200px" borderColor={t.border}>
                                <Text color={calendar.sharedFromEmail ? t.text : t.muted} fontSize="sm" sx={{ overflowWrap: 'anywhere' }}>
                                  {calendar.sharedFromEmail || 'Not shared in'}
                                </Text>
                              </Td>
                              <Td px={2} borderColor={t.border}>
                                <Text color={t.text} fontSize="sm" whiteSpace="nowrap">
                                  {calendar.totalEvents || 0} total
                                </Text>
                                <Text color={t.muted} fontSize="sm" whiteSpace="nowrap">
                                  {calendar.upcomingEvents || 0} upcoming
                                </Text>
                              </Td>
                              <Td px={2} borderColor={t.border}>
                                <Text color={t.muted} fontSize="sm" whiteSpace="nowrap">
                                  {createdLabel(calendar.createdAt)}
                                </Text>
                              </Td>
                              <Td px={2} borderColor={t.border}>
                                <SimpleGrid columns={2} spacing={1} minW="220px">
                                  <Tooltip label="Open this calendar in a new tab. Changes nothing." hasArrow openDelay={300}>
                                    <Button
                                      size="sm"
                                      minH="44px"
                                      fontSize="sm"
                                      variant="ghost"
                                      leftIcon={<FiExternalLink />}
                                      color={t.text}
                                      onClick={() => window.open(`/calendars/${calendar.id}/view`, '_blank')}
                                    >
                                      Open
                                    </Button>
                                  </Tooltip>
                                  <Tooltip label="Change this calendar's name, kind, colour and time zone on its own page (who can see it is on the Sharing tab there). Nothing is saved until you press Save." hasArrow openDelay={300}>
                                    <Button
                                      size="sm"
                                      minH="44px"
                                      fontSize="sm"
                                      variant="ghost"
                                      leftIcon={<FiEdit />}
                                      color={t.text}
                                      onClick={() => openCalendarPage(calendar, true)}
                                    >
                                      Edit
                                    </Button>
                                  </Tooltip>
                                  <Tooltip label="Share this calendar with other people and choose who can view or edit it. Opens a form first." hasArrow openDelay={300}>
                                    <Button
                                      size="sm"
                                      minH="44px"
                                      fontSize="sm"
                                      variant="ghost"
                                      colorScheme="green"
                                      leftIcon={<FiShare2 />}
                                      onClick={() => openShareModal(calendar)}
                                    >
                                      Share
                                    </Button>
                                  </Tooltip>
                                  <Tooltip label="Switch this calendar off so it disappears from every list and view (its events are kept). You are asked to confirm first; only the owner can delete it." hasArrow openDelay={300}>
                                    <Button
                                      size="sm"
                                      minH="44px"
                                      fontSize="sm"
                                      variant="ghost"
                                      colorScheme="red"
                                      leftIcon={<FiTrash2 />}
                                      onClick={() => handleDeleteCalendar(calendar)}
                                    >
                                      Delete
                                    </Button>
                                  </Tooltip>
                                </SimpleGrid>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CalSurface>
              </Box>

              {/* Cards — always on phones and tablets; on wide screens when chosen */}
              <Box display={{ base: 'block', lg: viewMode === 'cards' ? 'block' : 'none' }}>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={{ base: 3, md: 4 }}>
                  {paginatedCalendars.map((calendar: any) => renderCard(calendar))}
                </SimpleGrid>
              </Box>

              {/* Pagination — shared by table and cards */}
              <Stack
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'stretch', md: 'center' }}
                spacing={3}
              >
                <HStack spacing={3} flexWrap="wrap" rowGap={2}>
                  <Text color={t.muted} fontSize="sm">
                    Showing {showingFrom}–{showingTo} of {totalFiltered}
                  </Text>
                  <Tooltip label="How many calendars to show on each page. Changes only what you see." hasArrow openDelay={300}>
                    <Box>
                      <Select
                        aria-label="Calendars per page"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                        width="160px"
                        {...fieldProps}
                      >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                      </Select>
                    </Box>
                  </Tooltip>
                </HStack>
                <HStack spacing={2} justify={{ base: 'space-between', md: 'flex-end' }}>
                  <Tooltip shouldWrapChildren label="Go back to the previous page of calendars" hasArrow openDelay={300}>
                    <Button
                      size="lg"
                      minH="48px"
                      variant="outline"
                      leftIcon={<ChevronLeftIcon />}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      isDisabled={safePage === 1}
                    >
                      Previous
                    </Button>
                  </Tooltip>
                  <Text color={t.muted} fontSize="sm" whiteSpace="nowrap">
                    Page {safePage} of {totalPages}
                  </Text>
                  <Tooltip shouldWrapChildren label="Go to the next page of calendars" hasArrow openDelay={300}>
                    <Button
                      size="lg"
                      minH="48px"
                      variant="outline"
                      rightIcon={<ChevronRightIcon />}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      isDisabled={safePage >= totalPages}
                    >
                      Next
                    </Button>
                  </Tooltip>
                </HStack>
              </Stack>
            </>
          )}


          {/* Create Calendar Modal */}
          <Modal isOpen={isCreateOpen} onClose={onCreateClose} size={{ base: "full", md: "lg" }}>
            <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(10px)" />
            <ModalContent margin={{ base: 0, md: "auto" }} borderRadius={{ base: 0, md: "2xl" }}>
              <ModalHeader color={t.text} fontSize={{ base: "lg", md: "xl" }} pr={14}>
                Create a new calendar
              </ModalHeader>
              <ModalCloseButton size="lg" color={t.text} />
              <ModalBody pb={6}>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel {...labelProps}>Calendar name</FormLabel>
                    <Input
                      placeholder="For example: Team meetings"
                      value={newCalendarName}
                      onChange={(e) => setNewCalendarName(e.target.value)}
                      {...fieldProps}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel {...labelProps}>Description (optional)</FormLabel>
                    <Textarea
                      placeholder="What this calendar is for"
                      value={newCalendarDescription}
                      onChange={(e) => setNewCalendarDescription(e.target.value)}
                      bg={t.raised}
                      borderColor={t.borderStrong}
                      color={t.text}
                      fontSize="md"
                      _placeholder={{ color: t.faint }}
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel {...labelProps}>Type</FormLabel>
                    <Select value={newCalendarType} onChange={(e) => setNewCalendarType(e.target.value)} {...fieldProps}>
                      {calendarTypes.map((type) => (
                        <option key={type.name} value={type.name}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel {...labelProps}>Who can see it</FormLabel>
                    <Select value={newCalendarVisibility} onChange={(e) => setNewCalendarVisibility(e.target.value)} {...fieldProps}>
                      {calendarVisibilities.map((visibility) => (
                        <option key={visibility.name} value={visibility.name}>
                          {visibility.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel {...labelProps}>Colour</FormLabel>
                    <HStack spacing={2}>
                      <Input
                        type="color"
                        aria-label="Pick a colour"
                        value={newCalendarColor}
                        onChange={(e) => setNewCalendarColor(e.target.value)}
                        {...fieldProps}
                        w={16}
                        p={1}
                        flexShrink={0}
                      />
                      <Input
                        aria-label="Colour code"
                        value={newCalendarColor}
                        onChange={(e) => setNewCalendarColor(e.target.value)}
                        {...fieldProps}
                        flex={1}
                        minW={0}
                      />
                    </HStack>
                  </FormControl>

                  <FormControl>
                    <FormLabel {...labelProps}>Time zone</FormLabel>
                    <Select value={newCalendarTimezone} onChange={(e) => setNewCalendarTimezone(e.target.value)} {...fieldProps}>
                      <option value="Australia/Adelaide">Adelaide</option>
                      <option value="Australia/Brisbane">Brisbane</option>
                      <option value="Australia/Melbourne">Melbourne</option>
                      <option value="Australia/Perth">Perth</option>
                      <option value="Australia/Sydney">Sydney</option>
                      <option value="UTC">UTC (no local time)</option>
                    </Select>
                  </FormControl>

                  <HStack
                    spacing={3}
                    align="start"
                    bg={alpha(t.primary, 0.08)}
                    p={3}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={alpha(t.primary, 0.3)}
                  >
                    <Icon as={FiInfo} color={t.primary} mt={1} flexShrink={0} />
                    <Text color={t.text} fontSize="md">
                      This creates a new calendar that can be shared with clients and used for scheduling events.
                    </Text>
                  </HStack>

                  <Stack direction={{ base: 'column-reverse', sm: 'row' }} spacing={3} justify="flex-end" pt={2}>
                    <Tooltip label="Close without creating a calendar" hasArrow openDelay={300}>
                      <Button variant="ghost" size="lg" minH="48px" onClick={onCreateClose}>
                        Cancel
                      </Button>
                    </Tooltip>
                    <Tooltip shouldWrapChildren label={newCalendarName.trim() ? "Save and create this new calendar" : "Type a calendar name first"} hasArrow openDelay={300}>
                      <Button
                        colorScheme="blue"
                        size="lg"
                        minH="48px"
                        w={{ base: '100%', sm: 'auto' }}
                        leftIcon={<FiPlus />}
                        onClick={handleCreateCalendar}
                        isDisabled={!newCalendarName.trim()}
                      >
                        Create calendar
                      </Button>
                    </Tooltip>
                  </Stack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Calendar sharing is now managed via the Email module (Email Accounts Admin > Linked Calendars) */}

          {/* Transfer Ownership Modal */}
          <Modal isOpen={isTransferOpen} onClose={onTransferClose} size={{ base: "full", md: "lg" }}>
            <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(10px)" />
            <ModalContent margin={{ base: 0, md: "auto" }} borderRadius={{ base: 0, md: "2xl" }}>
              <ModalHeader color={t.text} fontSize={{ base: "lg", md: "xl" }} pr={14}>
                Transfer calendar ownership
                {selectedCalendar && (
                  <Text fontSize="md" fontWeight="normal" color={t.muted} mt={1} sx={{ overflowWrap: 'anywhere' }}>
                    {selectedCalendar.name}
                  </Text>
                )}
              </ModalHeader>
              <ModalCloseButton size="lg" color={t.text} />
              <ModalBody pb={6}>
                <VStack spacing={4} align="stretch">
                  {/* Current Owner */}
                  <Box>
                    <Text {...labelProps}>Current owner</Text>
                    {(() => {
                      const currentOwner = selectedCalendar?.responsibleOwnerId && clientsData?.clients?.find(
                        (c: any) => c.id === selectedCalendar.responsibleOwnerId
                      );
                      return selectedCalendar && !selectedCalendar.responsibleOwnerId ? (
                        <Box p={3} bg={t.raised} borderRadius="lg" borderWidth="1px" borderColor={t.border}>
                          <Text color={t.muted} fontSize="md">
                            No owner currently assigned
                          </Text>
                        </Box>
                      ) : currentOwner ? (
                        <Box p={3} bg={t.raised} borderRadius="lg" borderWidth="1px" borderColor={t.border}>
                          <VStack align="start" spacing={1}>
                            <HStack>
                              <Icon as={FiUser} color={t.faint} />
                              <Text color={t.text} fontSize="md" fontWeight="semibold">
                                {currentOwner.fName} {currentOwner.lName}
                              </Text>
                            </HStack>
                            {currentOwner.email && (
                              <Text color={t.muted} fontSize="sm" sx={{ overflowWrap: 'anywhere' }}>
                                {currentOwner.email}
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      ) : (
                        <Text color={t.muted} fontSize="md" sx={{ overflowWrap: 'anywhere' }}>
                          Owner ID {selectedCalendar?.responsibleOwnerId} (not in the client list)
                        </Text>
                      );
                    })()}
                  </Box>

                  {/* New Owner Selection */}
                  <FormControl>
                    <FormLabel {...labelProps}>New owner</FormLabel>
                    <Select
                      value={newOwnerId}
                      onChange={(e) => setNewOwnerId(e.target.value)}
                      {...fieldProps}
                    >
                      <option value="">No owner (unassigned)</option>
                      {clientsData?.clients
                        ?.filter((client: any) =>
                          // Don't show current owner
                          client.id !== selectedCalendar?.responsibleOwnerId
                        )
                        .map((client: any) => (
                          <option key={client.id} value={client.id}>
                            {client.fName} {client.lName} ({client.email})
                          </option>
                        ))}
                    </Select>
                  </FormControl>

                  {/* What happens */}
                  <Box bg={alpha(t.warning, 0.1)} p={3} borderRadius="lg" borderWidth="1px" borderColor={alpha(t.warning, 0.4)}>
                    <HStack spacing={2} mb={2}>
                      <Icon as={FiAlertTriangle} color={t.warning} />
                      <Text color={t.text} fontSize="md" fontWeight="semibold">
                        What happens when you transfer
                      </Text>
                    </HStack>
                    <VStack align="start" spacing={1} pl={6}>
                      <Text color={t.text} fontSize="md">• The new owner has full control over this calendar.</Text>
                      <Text color={t.text} fontSize="md">• The current owner keeps full admin access as a shared user.</Text>
                      <Text color={t.text} fontSize="md">• The new owner can remove anyone's access, including the previous owner.</Text>
                    </VStack>
                  </Box>

                  {/* Actions */}
                  <Stack direction={{ base: 'column-reverse', sm: 'row' }} spacing={3} justify="flex-end" pt={2}>
                    <Tooltip label="Close without changing the owner" hasArrow openDelay={300}>
                      <Button variant="ghost" size="lg" minH="48px" onClick={onTransferClose}>
                        Cancel
                      </Button>
                    </Tooltip>
                    <Tooltip label="Save: hand this calendar to the owner picked above" hasArrow openDelay={300}>
                      <Button
                        colorScheme="orange"
                        size="lg"
                        minH="48px"
                        w={{ base: '100%', sm: 'auto' }}
                        leftIcon={<FiUserCheck />}
                        onClick={handleTransferOwnership}
                      >
                        Transfer ownership
                      </Button>
                    </Tooltip>
                  </Stack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Share Calendar Modal */}
          {shareCalendar && (
            <ShareCalendarModal
              isOpen={isShareOpen}
              onClose={() => {
                onShareClose();
                setShareCalendar(null);
              }}
              calendarId={shareCalendar.id}
              calendarName={shareCalendar.name}
              onChanged={() => refetchCalendars()}
            />
          )}

          {/* External Calendar Modal */}
          <ExternalCalendarModal
            isOpen={isExternalOpen}
            onClose={onExternalClose}
            onSuccess={() => {
              refetchCalendars();
              refetchExternalCalendars();
              onExternalClose();
            }}
          />
        </VStack>
        {confirmDialog}
        <PoweredBy />
    </Box>
  );
};

export default CalendarAccountsAdmin;
